import { logout } from "@/api/user";
import { clearUserInfo, switchUserRole } from "@/utils/storage";
import { LogoutOutlined, UserOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { history, useModel } from "@umijs/max";
import type { MenuProps } from "antd";
import { Spin } from "antd";
import { createStyles } from "antd-style";
import React from "react";
import { flushSync } from "react-dom";
import HeaderDropdown from "../HeaderDropdown";

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel("@@initialState");
  const { currentUser } = initialState || {};

  // 显示用户名和角色名
  const displayName = currentUser?.username;
  const roleName = currentUser?.currentRoleName;

  return (
    <span className="anticon">
      {displayName}
      {roleName && <span style={{ color: '#999', marginLeft: '4px' }}>({roleName})</span>}
    </span>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: "flex",
      height: "48px",
      marginLeft: "auto",
      overflow: "hidden",
      alignItems: "center",
      padding: "0 8px",
      cursor: "pointer",
      borderRadius: token.borderRadius,
      "&:hover": {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await logout();
    clearUserInfo(); // 清空存储
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get("redirect");
    // Note: There may be security issues, please note
    if (window.location.pathname !== "/user/login" && !redirect) {
      history.replace({
        pathname: "/user/login",
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel("@@initialState");

  const onMenuClick: MenuProps["onClick"] = (event) => {
    const { key } = event;

    // 处理角色切换
    if (key.startsWith("role_")) {
      const roleId = parseInt(key.replace("role_", ""), 10);
      const role = currentUser?.roleList?.find((r: { roleId: number; roleName: string; roleKey: string }) => r.roleId === roleId);
      if (role) {
        switchUserRole(roleId, role.roleName);
      }
      return;
    }

    if (key === "logout") {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === "center") {
      history.push("/account/center");
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.username) {
    return loading;
  }

  // 构建角色切换菜单项
  const roleMenuItems = currentUser?.roleList && currentUser.roleList.length > 1
    ? [
      {
        type: "group" as const,
        label: "切换角色",
      },
      ...currentUser.roleList.map((role: { roleId: number; roleName: string; roleKey: string }) => ({
        key: `role_${role.roleId}`,
        icon: <UserSwitchOutlined />,
        label: role.roleId === currentUser.roleId
          ? <span><strong>{role.roleName}</strong> (当前)</span>
          : role.roleName,
        disabled: role.roleId === currentUser.roleId, // 当前角色不可点击
      })),
      {
        type: "divider" as const,
      },
    ]
    : [];

  const menuItems = [
    ...(menu
      ? [
        {
          key: "center",
          icon: <UserOutlined />,
          label: "个人中心",
        },
        // {
        //   key: 'settings',
        //   icon: <SettingOutlined />,
        //   label: '个人设置',
        // },
        {
          type: "divider" as const,
        },
      ]
      : []),
    ...roleMenuItems,
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
